import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Eye, Key, Trash2, Shield, User, Mail, Phone, 
  Save, X, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';

const SuperAdminManagementPage = () => {
  const navigate = useNavigate();
  
  // State for admin list
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for modals
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showViewDetails, setShowViewDetails] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // State for forms
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [deletePassword, setDeletePassword] = useState('');

  // Form validation states
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [passwordFormErrors, setPasswordFormErrors] = useState({});
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    setError('');
    try {
      // Call REAL backend API to fetch admin users
      const adminsUrl = `${window.location.origin}/api/admin/users/admins`;
      
      // Get current admin token for authentication
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Authentication required. Please login again.');
        setLoading(false);
        return;
      }
      
      console.log('Fetching admin users from API...');
      
      const response = await fetch(adminsUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to fetch admin users (${response.status})`);
      }
      
      const adminUsers = await response.json();
      console.log(`✅ Fetched ${adminUsers.length} admin users`);
      
      setAdmins(adminUsers);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError(err.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const validateCreateForm = () => {
    const errors = {};
    
    if (!createForm.firstName.trim()) errors.firstName = 'First name is required';
    if (!createForm.lastName.trim()) errors.lastName = 'Last name is required';
    if (!createForm.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(createForm.email)) errors.email = 'Email is invalid';
    if (!createForm.password) errors.password = 'Password is required';
    else if (createForm.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (createForm.password !== createForm.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!createForm.phone.trim()) errors.phone = 'Phone number is required';
    
    // Check if email already exists
    if (admins.some(admin => admin.email === createForm.email)) {
      errors.email = 'Admin with this email already exists';
    }
    
    setCreateFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordForm.newPassword) errors.newPassword = 'New password is required';
    else if (passwordForm.newPassword.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    
    setPasswordFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAdmin = async () => {
    if (!validateCreateForm()) return;
    
    try {
      // Call REAL backend API to create admin
      const adminCreateUrl = `${window.location.origin}/api/admin/users/create-admin`;
      
      // Get current admin token for authentication
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      const payload = {
        email: createForm.email,
        password: createForm.password,
        first_name: createForm.firstName,
        last_name: createForm.lastName,
        phone: createForm.phone,
        country_code: '+91',
        state: null,
        city: null
      };
      
      console.log('Creating admin user via API...', { email: payload.email });
      
      const response = await fetch(adminCreateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Admin creation failed (${response.status})`);
      }
      
      const newAdmin = await response.json();
      console.log('✅ Admin user created successfully:', newAdmin.email);
      
      // Add to local state
      setAdmins([...admins, newAdmin]);
      setShowCreateAdmin(false);
      setCreateForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: ''
      });
      setCreateFormErrors({});
      
      toast.success(`Admin user ${newAdmin.email} created successfully!`);
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.message || 'Failed to create admin user');
    }
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;
    
    try {
      // Call REAL backend API to change password
      const changePasswordUrl = `${window.location.origin}/api/admin/super-admin/change-password/${selectedAdmin.id}`;
      
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      const payload = {
        password: passwordForm.newPassword
      };
      
      console.log(`Changing password for admin: ${selectedAdmin.email}`);
      
      const response = await fetch(changePasswordUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Password change failed (${response.status})`);
      }
      
      console.log('✅ Password changed successfully');
      
      setShowChangePassword(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      setPasswordFormErrors({});
      setSelectedAdmin(null);
      
      toast.success('Admin password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    }
  };

  const handleDeleteAdmin = async () => {
    setDeleteError('');
    
    // Verify super admin password
    if (deletePassword !== 'Haute@123') {
      setDeleteError('Invalid super admin password');
      return;
    }

    if (selectedAdmin.email === 'superadmin@thehautecircle.com') {
      setDeleteError('Cannot delete the super admin account');
      return;
    }
    
    try {
      // Call REAL backend API to delete admin
      const deleteUrl = `${window.location.origin}/api/admin/super-admin/delete-admin/${selectedAdmin.id}`;
      
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      const payload = {
        password: deletePassword
      };
      
      console.log(`Deleting admin: ${selectedAdmin.email}`);
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Admin deletion failed (${response.status})`);
      }
      
      console.log('✅ Admin deleted successfully');
      
      // Remove from local state
      setAdmins(admins.filter(admin => admin.id !== selectedAdmin.id));
      
      setShowDeleteConfirm(false);
      setDeletePassword('');
      setDeleteError('');
      setSelectedAdmin(null);
      
      toast.success('Admin user deleted successfully!');
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.message || 'Failed to delete admin user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-600">Loading admin users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-red-600" />
                  Super Admin Den
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">Manage admin users and permissions</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateAdmin(true)}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              <Plus size={16} />
              Create New Admin
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Admin List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              All Admin Users ({admins.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Admin</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Contact</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Created</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Last Login</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{admin.name}</p>
                          <p className="text-sm text-gray-600">{admin.email}</p>
                          {admin.email === 'superadmin@thehautecircle.com' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                              <Shield size={12} className="mr-1" />
                              Super Admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-700">{admin.phone}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-700">{formatDate(admin.created_at)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-700">{formatDate(admin.last_login)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setShowViewDetails(true);
                            }}
                            className="flex items-center gap-1"
                          >
                            <Eye size={14} />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setShowChangePassword(true);
                            }}
                            className="flex items-center gap-1"
                          >
                            <Key size={14} />
                            Password
                          </Button>
                          {admin.email !== 'superadmin@thehautecircle.com' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowDeleteConfirm(true);
                              }}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Admin Modal */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Admin</DialogTitle>
            <DialogDescription>
              Create a new admin user with full access privileges
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm({...createForm, firstName: e.target.value})}
                  className={createFormErrors.firstName ? 'border-red-300' : ''}
                />
                {createFormErrors.firstName && (
                  <p className="text-xs text-red-600 mt-1">{createFormErrors.firstName}</p>
                )}
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm({...createForm, lastName: e.target.value})}
                  className={createFormErrors.lastName ? 'border-red-300' : ''}
                />
                {createFormErrors.lastName && (
                  <p className="text-xs text-red-600 mt-1">{createFormErrors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                className={createFormErrors.email ? 'border-red-300' : ''}
              />
              {createFormErrors.email && (
                <p className="text-xs text-red-600 mt-1">{createFormErrors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={createForm.phone}
                onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                className={createFormErrors.phone ? 'border-red-300' : ''}
              />
              {createFormErrors.phone && (
                <p className="text-xs text-red-600 mt-1">{createFormErrors.phone}</p>
              )}
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                className={createFormErrors.password ? 'border-red-300' : ''}
              />
              {createFormErrors.password && (
                <p className="text-xs text-red-600 mt-1">{createFormErrors.password}</p>
              )}
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={createForm.confirmPassword}
                onChange={(e) => setCreateForm({...createForm, confirmPassword: e.target.value})}
                className={createFormErrors.confirmPassword ? 'border-red-300' : ''}
              />
              {createFormErrors.confirmPassword && (
                <p className="text-xs text-red-600 mt-1">{createFormErrors.confirmPassword}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdmin(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAdmin} className="bg-red-600 hover:bg-red-700">
              <Save size={16} className="mr-2" />
              Create Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showViewDetails} onOpenChange={setShowViewDetails}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Admin Details</DialogTitle>
          </DialogHeader>
          {selectedAdmin && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700">Full Name</Label>
                <p className="text-gray-900">{selectedAdmin.name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Email Address</Label>
                <p className="text-gray-900">{selectedAdmin.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
                <p className="text-gray-900">{selectedAdmin.phone}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Created</Label>
                <p className="text-gray-900">{formatDate(selectedAdmin.created_at)}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Last Login</Label>
                <p className="text-gray-900">{formatDate(selectedAdmin.last_login)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowViewDetails(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Admin Password</DialogTitle>
            <DialogDescription>
              {selectedAdmin && `Change password for ${selectedAdmin.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                className={passwordFormErrors.newPassword ? 'border-red-300' : ''}
              />
              {passwordFormErrors.newPassword && (
                <p className="text-xs text-red-600 mt-1">{passwordFormErrors.newPassword}</p>
              )}
            </div>
            <div>
              <Label htmlFor="confirmNewPassword">Confirm Password</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                className={passwordFormErrors.confirmPassword ? 'border-red-300' : ''}
              />
              {passwordFormErrors.confirmPassword && (
                <p className="text-xs text-red-600 mt-1">{passwordFormErrors.confirmPassword}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePassword(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} className="bg-red-600 hover:bg-red-700">
              <Key size={16} className="mr-2" />
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Delete Admin User
            </DialogTitle>
            <DialogDescription>
              {selectedAdmin && `Are you sure you want to delete ${selectedAdmin.name}? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                Enter your Super Admin password to confirm deletion
              </p>
            </div>
            <div>
              <Label htmlFor="deletePassword">Super Admin Password</Label>
              <Input
                id="deletePassword"
                type="password"
                placeholder="Enter your password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className={deleteError ? 'border-red-300' : ''}
              />
              {deleteError && (
                <p className="text-xs text-red-600 mt-1">{deleteError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setDeletePassword('');
              setDeleteError('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteAdmin} className="bg-red-600 hover:bg-red-700">
              <Trash2 size={16} className="mr-2" />
              Delete Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminManagementPage;